/* ARSYNC Control total
=========================================================================================
* Descripción: Hook de reconocimiento de voz (Web Speech API del navegador) para el
*              dictado por micrófono de los buscadores. Uso "push-to-talk": se mantiene
*              presionado el botón, se acumula la transcripción y al SOLTAR se entrega
*              el texto completo, una sola vez, al onResult del consumidor.
*              - interimResults=true: se recorren todos los resultados del evento para
*                acumular fragmentos finales durante la pulsación.
*              - La entrega se hace en onend (al soltar); durante el dictado no se
*                escribe nada en el filtro del consumidor.
*              - stopListening() usa stop() y NO aborta, para no perder el resultado
*                final; si el navegador no emite onend, un timeout de seguridad hace
*                flush de lo acumulado.
*              - Si el motor cierra por una pausa de silencio con el dedo aún
*                presionado, la escucha se reinicia sobre el mismo búfer.
*              - maxDurationMs acota el dictado para no dejar el micrófono abierto.
*              - checkPermission no depende de isSupported en el primer render (antes
*                podía quedar hasPermission=false y el botón nunca aparecer).
*              No usa backend ni apiKey: el navegador envía el audio a su motor de voz.
*              Consumidores: ModalBuscarClientes, ModalBuscarEntradasSalidas,
*              ModalBuscarArticulosParaVenta, ModalBuscarArticulosSimple,
*              ModalBuscarAlmacenesExhibidores, ModalBuscarArticulosParaVentaPortatil
*              y FrmExistencias.
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 09/10/2026 | Lic. José Luis Aragón C.        | Creación
*      2       | 10/10/2026 | Lic. José Luis Aragón C.        | Push-to-talk: acumula mientras se presiona y entrega al soltar
* ---------------------------------------------------------------------------------------
*/

import { useState, useRef, useCallback, useEffect } from "react";

const useSpeechToText = ({
  lang = "es-MX",
  onResult,
  maxDurationMs = 30000,
  flushDelayMs = 350,
} = {}) => {
  const [isSupported, setIsSupported] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const onResultRef = useRef(onResult);
  const supportedRef = useRef(false);
  const langRef = useRef(lang);
  // Texto acumulado durante la pulsación actual.
  const bufferRef = useRef([]);
  const interimRef = useRef("");
  const entregadoRef = useRef(false);
  // El dedo/pulsación sigue activo: mientras sea true no se entrega nada.
  const holdRef = useRef(false);
  const stopPendienteRef = useRef(false);
  const flushTimeoutRef = useRef(null);
  const maxTimeoutRef = useRef(null);

  langRef.current = lang;

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  const limpiarTemporizadores = useCallback(() => {
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
      maxTimeoutRef.current = null;
    }
  }, []);

  // Entrega única del texto escuchado (finales + último provisional) al consumidor.
  const entregar = useCallback(() => {
    limpiarTemporizadores();
    if (entregadoRef.current) return;
    entregadoRef.current = true;

    const texto = [...bufferRef.current, interimRef.current]
      .map((parrafo) => (parrafo || "").trim())
      .filter((parrafo) => parrafo.length > 0)
      .join(" ")
      .trim();

    bufferRef.current = [];
    interimRef.current = "";

    if (texto.length > 0 && onResultRef.current) {
      onResultRef.current(texto);
    }
  }, [limpiarTemporizadores]);

  const checkPermission = useCallback(async () => {
    if (!supportedRef.current) return;
    try {
      const perm = await navigator.permissions.query({ name: "microphone" });
      setHasPermission(perm.state === "granted" || perm.state === "prompt");
      perm.onchange = () => {
        setHasPermission(perm.state === "granted");
      };
    } catch {
      setHasPermission(true);
    }
  }, []);

  const stopListening = useCallback(() => {
    holdRef.current = false;
    setIsListening(false);

    if (stopPendienteRef.current) return;
    const recognition = recognitionRef.current;
    if (!recognition) {
      entregar();
      return;
    }

    stopPendienteRef.current = true;
    try {
      // stop() (y no abort()) para que el motor cierre y emita el resultado final.
      recognition.stop();
    } catch {
      recognitionRef.current = null;
      stopPendienteRef.current = false;
      entregar();
      return;
    }

    // Red de seguridad: ante un stop() prematuro algunos navegadores no emiten onend.
    flushTimeoutRef.current = setTimeout(() => {
      flushTimeoutRef.current = null;
      try {
        recognition.abort();
      } catch {
        // el motor ya estaba cerrado: no hay nada que abortar
      }
      recognitionRef.current = null;
      stopPendienteRef.current = false;
      entregar();
    }, flushDelayMs);
  }, [entregar, flushDelayMs]);

  const startListening = useCallback(() => {
    if (!supportedRef.current || recognitionRef.current) return;

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    limpiarTemporizadores();
    bufferRef.current = [];
    interimRef.current = "";
    entregadoRef.current = false;
    stopPendienteRef.current = false;
    holdRef.current = true;
    setError(null);

    const recognition = new SpeechRecognition();
    recognition.lang = langRef.current;
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const alternativa = result && result[0];
        if (!alternativa) continue;

        if (result.isFinal) {
          bufferRef.current.push(alternativa.transcript);
          interimRef.current = "";
        } else {
          interimRef.current = alternativa.transcript;
        }
      }
    };

    recognition.onerror = (event) => {
      setError(event.error);
      setIsListening(false);
      // Sin permiso o sin red no hay transcripción confiable: se descarta lo acumulado.
      if (event.error === "not-allowed" || event.error === "service-not-allowed" || event.error === "network") {
        holdRef.current = false;
        entregadoRef.current = true;
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      recognitionRef.current = null;

      // El dedo sigue presionado y el motor cerró por una pausa de silencio:
      // se reanuda la escucha sobre el mismo búfer para no cortar el dictado.
      if (holdRef.current) {
        try {
          recognitionRef.current = recognition;
          recognition.start();
          setIsListening(true);
          return;
        } catch {
          recognitionRef.current = null;
        }
      }

      stopPendienteRef.current = false;
      entregar();
    };

    recognitionRef.current = recognition;
    recognition.start();

    if (maxDurationMs > 0) {
      maxTimeoutRef.current = setTimeout(() => stopListening(), maxDurationMs);
    }
  }, [limpiarTemporizadores, stopListening, maxDurationMs]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    supportedRef.current = !!SpeechRecognition;
    setIsSupported(!!SpeechRecognition);
  }, []);

  // Al desmontar (cierre de modal) se cancela todo sin entregar texto.
  useEffect(() => {
    return () => {
      holdRef.current = false;
      limpiarTemporizadores();
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          // el motor ya estaba cerrado
        }
        recognitionRef.current = null;
      }
    };
  }, [limpiarTemporizadores]);

  return {
    isSupported,
    hasPermission,
    isListening,
    error,
    startListening,
    stopListening,
    checkPermission,
  };
};

export default useSpeechToText;